import type { RitualCardProps } from "@/components/cards/ritualCard"
import { RitualCard } from "@/components/cards/ritualCard"
import { Button } from "@/components/ui/button"
import { type IntentionRitual, RITUAL_FORMAT_LABELS } from "../types"

function mapRitualToCardProps(
  ritual: IntentionRitual,
  onBookmark?: (id: string) => void,
): RitualCardProps {
  const user = ritual.user
  const username = user?.name ?? user?.username ?? "Unknown"

  const tags: string[] = [
    ritual.isPaid ? "Paid" : "Free",
    RITUAL_FORMAT_LABELS[ritual.ritualType] ?? ritual.ritualType,
  ]

  if (ritual.city && ritual.state) {
    tags.push(`${ritual.city}, ${ritual.state}`)
  }

  let durationStr = ""
  if (ritual.duration) {
    durationStr = `${ritual.duration} ${ritual.durationType || "min"}`
  }

  return {
    id: ritual.id,
    image: ritual.coverImage ?? "",
    userAvatar: user?.profilePictureUrl ?? undefined,
    username,
    userHandle: "",
    title: ritual.title,
    tags,
    duration: durationStr,
    isBookmarked: ritual.isSaved,
    onBookmarkClick: onBookmark ? () => onBookmark(ritual.id) : undefined,
    userId: user?.id,
  }
}

interface IntentionRitualGridProps {
  rituals: IntentionRitual[]
  isLoading: boolean
  hasMore: boolean
  onLoadMore: () => void
  onBookmark?: (ritualId: string) => void
}

export function IntentionRitualGrid({
  rituals,
  isLoading,
  hasMore,
  onLoadMore,
  onBookmark,
}: IntentionRitualGridProps) {
  if (!isLoading && rituals.length === 0) {
    return (
      <div className="py-16 text-center">
        <p className="text-white/60 text-lg">No rituals found for this intention.</p>
        <p className="text-white/40 text-sm mt-2">Try adjusting your filters or search query.</p>
      </div>
    )
  }

  return (
    <div className="py-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {rituals.map(ritual => (
          <div key={ritual.id} className="h-[350px]">
            <RitualCard {...mapRitualToCardProps(ritual, onBookmark)} />
          </div>
        ))}
      </div>

      {/* Loading skeleton */}
      {isLoading && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
          <div className="h-[350px] bg-white/5 rounded-lg animate-pulse" aria-hidden="true" />
          <div className="h-[350px] bg-white/5 rounded-lg animate-pulse" aria-hidden="true" />
        </div>
      )}

      {/* Load More button */}
      {hasMore && !isLoading && (
        <div className="flex justify-center mt-8 mb-4">
          <Button
            variant="outline"
            onClick={onLoadMore}
            className="min-w-[200px] border-white/30 text-white hover:bg-white/10 rounded-full uppercase tracking-wider text-sm font-semibold"
          >
            Load More
          </Button>
        </div>
      )}
    </div>
  )
}
